import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
// material
import { alpha, styled } from '@mui/material/styles';
import { Button, Typography, Stack, Container, Paper } from '@mui/material';
import { varFadeInUp, varWrapEnter, varFadeInRight } from '../../animate';
import { ButtonContentProps } from '../../../@types/components-overview';
import { Box, useTheme } from '@mui/system';
import { MotionViewFadeUp } from '../../MotionViewFadeUp';

// ----------------------------------------------------------------------

const RootStyle = styled(motion.div)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  zIndex: 8,
  top: 0,
  display: 'block',
  height: 'auto'
}));

const ContentStyle = styled(motion.div)(({ theme }) => ({
  zIndex: 10,
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  height: '90vh'
}));
// ----------------------------------------------------------------------

export interface LandingPageHeroProps {
  title?: string;
  titlePrimary?: string;
  subtitle?: string;
  text?: string;
  minimalText?: string;
  image: string;
  overlay: string;
  buttons?: Array<ButtonContentProps>;
}

export default function LandingPageHero({
  text,
  subtitle,
  buttons,
  title,
  titlePrimary,
  image
}: LandingPageHeroProps) {
  const theme = useTheme();

  return (
    <RootStyle
      sx={{
        ...{
          backgroundColor: image
            ? (theme) => alpha(theme.palette.common.black, 0.9)
            : theme.palette.common.white,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundImage: `url(${image})`,
          backgroundPositionX: '63%',
          backgroundPositionY: 'top'
        }
      }}
      initial="initial"
      animate="animate"
      variants={varWrapEnter}
    >
      <Container maxWidth={'lg'}>
        <ContentStyle variants={varFadeInUp}>
          <Paper
            sx={{ background: 'transparent' }}
            component={Stack}
            direction="column"
            justifyContent="center"
          >
            <Container maxWidth={'lg'}>
              <Stack spacing={5}>
                <MotionViewFadeUp>
                  <>
                    {title && (
                      <Typography
                        variant="h2"
                        sx={{
                          color: 'common.white',
                          display: {
                            xl: 'block',
                            lg: 'block',
                            md: 'block',
                            sm: 'none',
                            xs: 'none'
                          }
                        }}
                      >
                        {title}
                        {titlePrimary && (
                          <Typography
                            variant="h2"
                            style={{
                              maxWidth: '70%',
                              color: theme.palette.primary.main
                            }}
                          >
                            {titlePrimary}
                          </Typography>
                        )}
                      </Typography>
                    )}
                    {title && (
                      <Typography
                        variant="h4"
                        sx={{
                          color: 'common.white',
                          display: {
                            xl: 'none',
                            lg: 'none',
                            md: 'none',
                            sm: 'block',
                            xs: 'block'
                          }
                        }}
                      >
                        {title}
                        {titlePrimary && (
                          <Typography
                            variant="h4"
                            style={{
                              maxWidth: '70%',
                              color: theme.palette.primary.main
                            }}
                          >
                            {titlePrimary}
                          </Typography>
                        )}
                      </Typography>
                    )}
                  </>
                </MotionViewFadeUp>
                <MotionViewFadeUp>
                  {subtitle && (
                    <Typography sx={{ color: 'common.white', fontWeight: 'bold' }}>
                      {subtitle}
                    </Typography>
                  )}
                </MotionViewFadeUp>
                <MotionViewFadeUp>
                  {text && <Typography sx={{ color: 'common.white' }}>{text}</Typography>}
                </MotionViewFadeUp>
                {buttons && (
                  <motion.div variants={varFadeInRight}>
                    {buttons.length > 0 && (
                      <Stack
                        direction={{
                          sm: 'column',
                          xs: 'column',
                          md: 'row'
                        }}
                        spacing={3}
                      >
                        {buttons.map((button) => (
                          <Button
                            key={buttons.indexOf(button)}
                            size={button.size}
                            color={button.color}
                            variant={button.variant}
                            component={RouterLink}
                            to={button.to}
                          >
                            {button.text}
                          </Button>
                        ))}
                      </Stack>
                    )}
                  </motion.div>
                )}
              </Stack>
            </Container>
          </Paper>
          <Box sx={{ margin: '0 0 2em 0' }} />
        </ContentStyle>
      </Container>
    </RootStyle>
  );
}
